import type {ReactElement} from 'react';
import React from 'react';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {
  isFacebookAccount,
  isGoogleBusinessProfileAccount,
  isInstagramAccount,
  isLinkedinAccount,
  isPinterestAccount,
  isThreadsAccount,
  isTwitterAccount,
} from '@Components/social-media/account.vo';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import {SocialThumbnail} from '@Components/social-media/components/social-thumbnail';
import {Text, TextSize} from '@Components/text';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import styles from './social-post-content-top-panel.module.scss';

export interface SocialPostContentTopPanelProps {
  account: SocialAccount;
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>;
  platformMediaType: PlatformMediaType;
}

export function SocialPostContentTopPanel({publishingParams, account, platformMediaType}: SocialPostContentTopPanelProps): ReactElement<any> {
  const publishingParamsForAccount = publishingParams[account.id];
  const isVideo = false;

  const renderPostInfo = (): ReactElement<any> => {
    const isHorizontal = isTwitterAccount(account) || isInstagramAccount(account) || isThreadsAccount(account);

    return (
      <div className={`${isHorizontal ? `${styles.postInfoHorizontal} _fit-width _full-height` : `${styles.postInfoVertical} _fit-width _full-height`}`}>
        <Text val={`${account?.name}`} bold />
        {renderPostDetailsByPlatform()}
      </div>
    );
  };

  const renderPostDetailsByPlatform = (): ReactElement<any> | null => {
    const textClassName = 'content-sub-text';

    if (isFacebookAccount(account) || isGoogleBusinessProfileAccount(account)) {
      return <Text val={`${window.i18next.t('pmwjs_time_placeholder')}`} className={textClassName} size={TextSize.XXSMALL} />;
    }

    if (isInstagramAccount(account)) {
      return <Text val={`. ${window.i18next.t('pmwjs_time_placeholder')}`} className={`${textClassName} spacing-m-l-1`} size={TextSize.XXSMALL} />;
    }

    if (isTwitterAccount(account)) {
      return (
        <Text
          val={`${account?.username ? `${account?.username} . ${window.i18next.t('pmwjs_time_placeholder')}` : `${window.i18next.t('pmwjs_time_placeholder')}`}`}
          className={`${textClassName} ${account?.username ? '' : 'spacing-m-l-2'}`}
          size={TextSize.XXSMALL}
        />
      );
    }

    if (isThreadsAccount(account)) {
      return (
        <Text
          val={`${account?.username ? `${account?.username} . ${window.i18next.t('pmwjs_time_placeholder')}` : `${window.i18next.t('pmwjs_time_placeholder')}`}`}
          className={`${textClassName} ${account?.username ? '' : 'spacing-m-l-2'}`}
          size={TextSize.XXSMALL}
        />
      );
    }

    if (isLinkedinAccount(account)) {
      return (
        <>
          <Text val={`${window.i18next.t('pmwjs_placeholder_linkedin_designation')}`} className="content-sub-text" size={TextSize.XXSMALL} />
          <Text val={`${window.i18next.t('pmwjs_time_placeholder')}`} className="content-sub-text" size={TextSize.XXSMALL} />
        </>
      );
    }

    return null;
  };

  const shouldShowTitle = (): boolean => {
    if (isPinterestAccount(account)) {
      return false;
    }

    if (platformMediaType === PlatformMediaType.STORIES) {
      return false;
    }

    if (!publishingParams[account.id]) {
      return false;
    }

    const activeParams = publishingParams[account.id][platformMediaType];

    if (!activeParams) {
      return false;
    }

    const {graphicsMeta} = activeParams;
    if (graphicsMeta === undefined) {
      return false;
    }

    const numberPosters = Object.keys(graphicsMeta).length;
    return account.type === SocialPlatform.TIKTOK && !isVideo && numberPosters === 1;
  };

  const getTitleFromParams = (): string => {
    if (platformMediaType === PlatformMediaType.STORIES) {
      return '';
    }

    const activeParams = publishingParamsForAccount[platformMediaType];
    if (activeParams && 'title' in activeParams && activeParams.title !== '') {
      return activeParams.title;
    }

    return window.i18next.t('pmwjs_title_placeholder');
  };

  const renderTitleHolder = (): ReactElement<any> | null => {
    if (shouldShowTitle()) {
      const textClasses = `color-neutral-28 ${styles.overflowingCaptions}`;
      return <Text val={`${getTitleFromParams()}`} bold className={textClasses} size={TextSize.XSMALL} />;
    }

    return null;
  };

  const getCaptionFromParams = (): string => {
    const publishingParamsForMediaType = publishingParamsForAccount[platformMediaType];

    if (platformMediaType === PlatformMediaType.STORIES) {
      return '';
    }

    if (!publishingParamsForMediaType) {
      return '';
    }

    if ('caption' in publishingParamsForMediaType && publishingParamsForMediaType.caption !== '') {
      return publishingParamsForMediaType.caption;
    }

    if (isThreadsAccount(account)) {
      return window.i18next.t('pmwjs_write_a_caption');
    }

    return window.i18next.t('pmwjs_caption_placeholder');
  };

  const renderPostCaption = (): ReactElement<any> => {
    const textClasses = `color-neutral-28 ${styles.overflowingCaptions}`;
    return <Text val={`${getCaptionFromParams()}`} className={textClasses} size={TextSize.XSMALL} />;
  };

  const renderCaptionHolder = (): ReactElement<any> | null => {
    if (isPinterestAccount(account) || isThreadsAccount(account)) {
      return null;
    }

    if (!isInstagramAccount(account) && !isGoogleBusinessProfileAccount(account)) {
      return <div className={`${styles.captionHolder} _full-width _fit-height spacing-m-b-3`}>{renderPostCaption()}</div>;
    }

    return null;
  };

  const getPostInfo = (): ReactElement<any> | null => {
    if (isPinterestAccount(account)) {
      return (
        <div className={`${styles.topPanel} ${styles.pinterest}`}>
          <SocialThumbnail isActive={false} type={account?.type} />
        </div>
      );
    }

    if (isThreadsAccount(account)) {
      return (
        <div className={`${styles.topPanel} spacing-m-b-2`}>
          <div className="flex-h-row">
            <div className="spacing-m-r-2 spacing-m-t-1">
              <SocialProfileIcon account={account} size={SocialProfileIconSize.LARGE} showPlatformIcon={false} />{' '}
            </div>
            <div className="flex-column">
              <div className="spacing-m-b-1">{renderPostInfo()}</div>
              {renderPostCaption()}
            </div>
          </div>
          <div className={styles.threadsThumbnail}>
            <SocialThumbnail isActive={false} type={account?.type} />
          </div>
        </div>
      );
    }

    const containerSpacing = isGoogleBusinessProfileAccount(account) ? 'spacing-p-3' : 'spacing-m-b-3';

    return (
      <div className={`${styles.topPanel} ${containerSpacing}`}>
        <div className="flex-row-align-center">
          <div className="spacing-m-r-2">
            <SocialProfileIcon account={account} size={SocialProfileIconSize.LARGE} showPlatformIcon={false} />{' '}
          </div>
          {renderPostInfo()}
        </div>
        <div className="flex-row-justify-center flex-items-center">
          <SocialThumbnail isActive={false} type={account?.type} />
        </div>
      </div>
    );
  };

  return (
    <>
      {getPostInfo()}
      {renderTitleHolder()}
      {renderCaptionHolder()}
    </>
  );
}
